import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import SEO from '../components/common/SEO';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PageContentBlocks from '../components/common/PageContentBlocks';
import usePageContent from '../hooks/usePageContent';
import { lessonsAPI, categoriesAPI } from '../services/api';

export default function LearnPage() {
  const { t, i18n } = useTranslation();
  const { blocks } = usePageContent('learn', i18n.language);
  const [lessons, setLessons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [difficulty, setDifficulty] = useState('');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const lang = i18n.language;
        const [lessonsRes, catsRes] = await Promise.all([
          lessonsAPI.getAll({ language: lang, search, categoryId, difficultyLevel: difficulty }),
          categoriesAPI.getAll({ language: lang }),
        ]);
        setLessons(lessonsRes.data.data);
        setCategories(catsRes.data.data);
      } catch { /* handled by empty state */ }
      finally { setLoading(false); }
    };
    const debounce = setTimeout(fetch, 300);
    return () => clearTimeout(debounce);
  }, [i18n.language, search, categoryId, difficulty]);

  return (
    <>
      <SEO title={t('learn.title')} />
      <section className="py-12 px-4 max-w-7xl mx-auto">
        <h1 className="section-title mb-8">{t('learn.title')}</h1>

        <PageContentBlocks blocks={blocks} className="mb-10" />

        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('learn.search')} className="input-field flex-1" />
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="input-field md:w-48">
            <option value="">{t('learn.allCategories')}</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.icon} {c.title}</option>)}
          </select>
          <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} className="input-field md:w-40">
            <option value="">{t('learn.difficulty')}</option>
            <option value="beginner">{t('learn.beginner')}</option>
            <option value="intermediate">{t('learn.intermediate')}</option>
            <option value="advanced">{t('learn.advanced')}</option>
          </select>
        </div>

        {loading ? <LoadingSpinner className="py-20" /> : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lessons.length === 0 ? (
              <p className="col-span-full text-center text-gray-500 py-12">{t('common.noResults')}</p>
            ) : lessons.map((lesson, i) => (
              <motion.div key={lesson.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="card group">
                <div className="flex items-start justify-between">
                  <span className="text-2xl">{lesson.categoryIcon || '📚'}</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-brand-100 dark:bg-brand-900 text-brand-700 dark:text-brand-300 capitalize">
                    {lesson.difficultyLevel}
                  </span>
                </div>
                <h3 className="font-bold text-lg mt-3 group-hover:text-brand-600 transition-colors">{lesson.title}</h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-2 line-clamp-2">{lesson.shortDescription}</p>
                <Link to={`/learn/${lesson.id}`} className="inline-block mt-4 text-brand-600 font-semibold text-sm hover:underline">
                  {t('learn.readMore')} →
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
